import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import AccordionItem from './AccordionItem';
import { HeadingLevel } from '@gik/ui';

export interface IAccordionItem {
  title: React.ReactNode;
  content: React.ReactNode;
  isOpen?: boolean;
}

export interface IAccordionProps extends React.HTMLAttributes<HTMLDivElement> {
  autoCloseItems?: boolean;
  items?: IAccordionItem[];
  dividers?: boolean;
  allToggled?: boolean;
  /**
   * Use this prop to force all items to recalculate height.
   * Useful when item's content height changes at some later point in time.
   * e.g. validation messages show/hide, collapsing on a breakpoint
   */
  recalculateHeightCallback?: (fn: () => void) => void;
  headingLevel?: HeadingLevel;
}

export default function Accordion({
  children,
  autoCloseItems = true,
  className,
  items,
  dividers,
  allToggled,
  recalculateHeightCallback,
  headingLevel,
  ...otherProps
}: IAccordionProps): React.ReactElement {
  const [activeIndices, setActiveIndices] = React.useState<number[]>([]);

  const blockRef = React.useRef<HTMLDivElement>(null);

  const bem = bemBlock('accordion');
  const blockClasses = bem(null, [{ dividers }], className);

  React.useEffect(() => {
    recalculateHeightCallback && recalculateHeightCallback(_recalculateHeight);
  }, [recalculateHeightCallback]);

  // recalculate height for all expanded items
  function _recalculateHeight() {
    blockRef.current &&
      Array.from(blockRef.current.childNodes).forEach((node, index) => {
        const el = node as HTMLDivElement;
        const mainEl = el.lastChild as HTMLDivElement;

        const itemIsOpen = activeIndices.indexOf(index) > -1;

        if (mainEl && itemIsOpen) {
          mainEl.style.height = 'auto';

          setTimeout(() => {
            // NOTE: this line needs to be inside setTimeout too
            // transition takes time, let it run and then sample the height
            // most transitions are ~300ms, larger timeout is needed
            const contentHeight = mainEl.offsetHeight;
            mainEl.style.height = contentHeight + 'px';
          }, 500);
        }
      });
  }

  function collapseAll() {
    if (!blockRef.current) {
      return;
    }

    Array.from(blockRef.current.childNodes).forEach(node => {
      const el = node as HTMLDivElement;
      const mainEl = el.lastChild as HTMLDivElement;
      if (mainEl) {
        mainEl.style.height = '0px';
      }
    });
    setActiveIndices([]);
  }

  function openAll() {
    if (!blockRef.current) {
      return;
    }

    const newActiveIndices = [];

    Array.from(blockRef.current.childNodes).forEach((node, index) => {
      const el = node as HTMLDivElement;
      const mainEl = el.lastChild as HTMLDivElement;
      if (mainEl) {
        mainEl.style.height = 'auto';
        const contentHeight = mainEl.offsetHeight;
        mainEl.style.height = '0px';

        newActiveIndices.push(index);
        // NOTE: small timeout is needed to trigger the height transition
        setTimeout(() => {
          mainEl.style.height = contentHeight + 'px';
        }, 1);
      }
    });
    setActiveIndices(newActiveIndices);
  }

  function handleToggle(ev: React.MouseEvent<HTMLDivElement>, index: number) {
    const itemEl = (ev.currentTarget as HTMLDivElement).parentElement;
    const mainEl = itemEl.lastChild as HTMLDivElement;

    const itemIsOpen = activeIndices.indexOf(index) > -1;

    let newActiveIndices = JSON.parse(JSON.stringify(activeIndices));

    if (itemIsOpen) {
      mainEl.style.height = '0px';
      newActiveIndices.splice(newActiveIndices.indexOf(index), 1);
    } else {
      if (autoCloseItems) {
        collapseAll();
        newActiveIndices = [];
      }

      mainEl.style.height = 'auto';
      const contentHeight = mainEl.offsetHeight;
      mainEl.style.height = '0px';

      // NOTE: small timeout is needed to trigger the height transition
      setTimeout(() => {
        mainEl.style.height = contentHeight + 'px';
      }, 1);
      newActiveIndices = newActiveIndices.concat([index]);
    }

    setActiveIndices(newActiveIndices);
  }

  React.useEffect(() => {
    if (allToggled === false) {
      collapseAll();
    } else if (allToggled === true) {
      openAll();
    }
  }, [allToggled]);

  return (
    <div className={blockClasses} {...otherProps} ref={blockRef}>
      {items?.map((item, index) => {
        const isOpen = activeIndices.indexOf(index) > -1;

        return (
          <AccordionItem
            key={index}
            header={item.title}
            isOpen={isOpen}
            onToggle={(ev: React.MouseEvent<HTMLDivElement>) => handleToggle(ev, index)}
            headerAsHeading={headingLevel !== undefined}
            headingLevel={headingLevel}
          >
            {item.content}
          </AccordionItem>
        );
      })}

      {!items &&
        React.Children.map(children, (child, index) => {
          if (!React.isValidElement(child)) {
            return null;
          }
          const isOpen = activeIndices.indexOf(index) > -1;

          const itemProps = {
            onToggle: (ev: React.MouseEvent<HTMLDivElement>) => handleToggle(ev, index),
            isOpen,
          };
          return (
            <>
              {dividers && index > 0 && <hr />}
              {React.isValidElement(child) && React.cloneElement(child, itemProps)}
            </>
          );
        })}
    </div>
  );
}
